import {
  createFileRoute,
  Outlet,
  useMatchRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { Divider, Link, Stack, Tabs, Typography, useMediaQuery, useTheme } from "@mui/material";
import { PageHeader } from "@/shared/components/PageHeader";
import { PaxTab, useClosableTabs, useTranslation } from "@ncino/pax-ui";
import { BuyRouteContext } from "./-internal/BuyRouteContext";
import { useEffect, useState } from "react";
import {
  useGetPoolsForBuyPageQuery,
  useGetUserBuyFiltersQuery,
} from "@/services/graphql/reactQuery";
import { BuyFilterSearch } from "./-internal/types";
import { useCanCreateIOIQuery } from "@/services/auth/permissions";
import { ShoppingCartIcon } from "@phosphor-icons/react";
import { LookingToBuyModal } from "@/routes/buy/-internal/lookingToBuyModal";
import { useSidebarExpanded } from "@/shared/hooks/useSidebarExpanded";

function RouteComponent() {
  const { t } = useTranslation("buy");
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [browseSearch, setBrowseSearch] = useState<BuyFilterSearch>({});
  const matchRoute = useMatchRoute();
  const { poolId } = useParams({ strict: false });
  const currentPoolId = poolId ?? null;
  const onInterested = !!matchRoute({ to: "/buy/interested" });
  const [anchorTab, setAnchorTab] = useState<"browse" | "interested">(
    onInterested ? "interested" : "browse"
  );

  const { data, isLoading } = useGetPoolsForBuyPageQuery({
    isActiveOnly: true,
  });

  const { data: userFilters } = useGetUserBuyFiltersQuery();

  const { data: canCreateIOI = false } = useCanCreateIOIQuery();

  const [lookingToBuyOpen, setLookingToBuyOpen] = useState<boolean>(false);

  const [isSidebarExpanded] = useSidebarExpanded();

  const {
    tabs: openPoolTabs,
    active: activePoolTab,
    setActive: setActivePoolTab,
    closeTab,
    addTab: addPoolTab,
  } = useClosableTabs<string>({
    initialTabs: currentPoolId ? [currentPoolId] : undefined,
    onTabClose: ({ wasActive, wasFinalTab, newActiveTabId }) => {
      if (!wasActive) {
        return;
      }
      if (wasFinalTab || !newActiveTabId) {
        if (anchorTab === "interested") {
          navigate({ to: "/buy/interested" });
        } else {
          navigate({ to: "/buy", search: browseSearch });
        }
      } else {
        navigate({
          to: "/buy/$poolId",
          params: { poolId: newActiveTabId },
          search: { tab: "tearsheet" },
        });
      }
    },
  });

  useEffect(() => {
    if (!currentPoolId) return;
    // URL matches a pool that's no longer open — re-add it (e.g. browser back after closing)
    if (!openPoolTabs.includes(currentPoolId)) {
      addPoolTab(currentPoolId);
      // URL changed but the wrong tab is highlighted — sync the active state
    } else if (activePoolTab !== currentPoolId) {
      setActivePoolTab(currentPoolId);
    }
  }, [currentPoolId]);

  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const getPoolLabel = (poolId: string) =>
    data?.pools.find((p) => p.id === poolId)?.poolNumber ?? poolId;

  return (
    <>
      <PageHeader
        title={t("buyPage.title")}
        subheader={t("buyPage.subtitle")}
        removeBottomPadding
        actionsRight={
          canCreateIOI ? (
            <Typography
              variant="body1"
              width={{
                // small screen shenanigans that should probably be fixed in pax-ui and not here
                xs: "100%",
                sm: `${isSidebarExpanded ? "30" : "45"}vw`,
                md: `${isSidebarExpanded ? "30" : "45"}vw`,
                lg: `45vw`,
              }}
              align={isSmall ? "left" : "right"}
            >
              {t("buyPage.lookingToBuyTitle") + " "}
              <Link
                variant="body2"
                component="button"
                type="button"
                onClick={() => {
                  setLookingToBuyOpen(true);
                }}
                underline="none"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: theme.typography.fontWeightMedium,
                }}
              >
                {t("buyPage.lookingToBuyLink") + " ->"}
              </Link>
            </Typography>
          ) : undefined
        }
        centerActionsRight
        actionsBottom={
          <Stack direction="row" alignItems="center">
            <Tabs
              value={currentPoolId ? false : anchorTab}
              aria-label={t("buyPage.buyTabs")}
              onChange={(_, value) => {
                setActivePoolTab(undefined);
                setAnchorTab(value);
                if (value === "browse") {
                  navigate({ to: "/buy", search: browseSearch });
                } else {
                  navigate({ to: "/buy/interested" });
                }
              }}
              sx={{ flexShrink: 0 }}
            >
              <PaxTab fillOnSelect label={t("buyPage.browseTab")} value="browse" />
              <PaxTab fillOnSelect label={t("buyPage.interestedTab")} value="interested" />
            </Tabs>

            {openPoolTabs.length > 0 && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mx: 1, alignSelf: "center", whiteSpace: "nowrap" }}
                >
                  {t("buyPage.selectedPoolsLabel")}
                </Typography>
                <Tabs
                  value={currentPoolId && activePoolTab ? activePoolTab : false}
                  variant="scrollable"
                  onChange={(_, value) => {
                    setActivePoolTab(value);
                    navigate({
                      to: "/buy/$poolId",
                      params: { poolId: value },
                      search: { tab: "tearsheet" },
                    });
                  }}
                >
                  {openPoolTabs.map((poolId) => (
                    <PaxTab
                      key={poolId}
                      value={poolId}
                      label={getPoolLabel(poolId)}
                      closeIcon
                      fillOnSelect
                      onClose={() => {
                        closeTab(poolId);
                      }}
                      onMiddleClick={() => {
                        closeTab(poolId);
                      }}
                    />
                  ))}
                </Tabs>
              </>
            )}
          </Stack>
        }
      />
      <BuyRouteContext.Provider
        value={{
          addTab: addPoolTab,
          openTabs: openPoolTabs,
          poolQuery: data,
          queryLoading: isLoading,
          page,
          setPage,
          userFilters: userFilters,
          browseSearch,
          setBrowseSearch,
        }}
      >
        <Outlet />
      </BuyRouteContext.Provider>
      <LookingToBuyModal
        key={`ltb-${lookingToBuyOpen}`}
        open={lookingToBuyOpen}
        onClose={() => setLookingToBuyOpen(false)}
      />
    </>
  );
}

export const Route = createFileRoute("/buy")({
  component: RouteComponent,
  staticData: {
    breadcrumb: {
      title: "Buy",
      icon: ShoppingCartIcon,
    },
  },
});
